import { isObject, isString, snakeCase, startCase, without } from 'lodash';

import { Data } from '@shared/api/types';

import { ColumnConfiguration, FilterConfiguration } from './DataTable.types';
import { DataTableFilters } from './DataTableFilters';
import { DataTableFooter } from './DataTableFooter';
import { DataTableHeader } from './DataTableHeader';
import { DataTableRow } from './DataTableRow';

type DataTableProps<T> = {
  data: Data<T>;
  columns: ColumnConfiguration<T>[];
  filters?: FilterConfiguration[];
  avoidPagination?: boolean;
  withoutFilters?: boolean;
  editUrl?: (row: T) => string;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
};

export function DataTable<T>({
  data,
  columns: allColumns,
  filters: additionalFilters = [],
  avoidPagination = false,
  withoutFilters = false,
  editUrl,
  onEdit,
  onDelete,
}: DataTableProps<T>) {
  const rows = data.data || [];
  const hasActions = Boolean(editUrl || onEdit || onDelete);

  const columns = allColumns.filter((column) => column.visible !== false);

  let filters: FilterConfiguration[] = [];

  if (!withoutFilters) {
    filters = columns
      .filter((column) => isObject(column.filterable) || (column.filterable && column.name))
      .map((column) => {
        const name = isString(column.filterable) ? column.filterable : snakeCase(column.name);
        const label = column.label || startCase(column.name);

        if (isObject(column.filterable)) {
          return { name, label, ...column.filterable } as FilterConfiguration;
        }

        return { name, label, type: 'text' } as FilterConfiguration;
      })
      .concat(additionalFilters.filter((filter) => filter.visible !== false));
  }

  return (
    <div>
      {filters.length > 0 && (
        <DataTableFilters filters={filters} query={data.query} onQueryChange={data.onQueryChange} />
      )}
      <div className="w-full overflow-x-scroll">
        <table className="min-w-full tabular-nums table-fixed">
          <DataTableHeader
            columns={columns}
            hasActions={hasActions}
            query={data.query}
            onQueryChange={data.onQueryChange}
          />
          <tbody>
            {rows.map((row, index) => (
              <DataTableRow
                key={index}
                row={row}
                columns={columns}
                refresh={data.refresh}
                editUrl={editUrl ? editUrl(row) : undefined}
                onEdit={onEdit ? () => onEdit(row) : undefined}
                onDelete={onDelete ? () => onDelete(row) : undefined}
              />
            ))}
          </tbody>
        </table>
      </div>
      <DataTableFooter
        results={data.results}
        pages={data.pages}
        avoidPagination={avoidPagination}
        query={data.query}
        onQueryChange={data.onQueryChange}
      />
    </div>
  );
}
